import { logError } from '@mop/shared/utils/logger';
type MemoryCacheEntry<T> = {
  data: T;
  expiresAt: number;
};

// Single map for both resolved cache entries and in-flight promises
const memoryCache = new Map<string, MemoryCacheEntry<any> | Promise<any>>();
const FIVE_MIN_IN_MS = 5 * 60 * 1000;

const getNextIntervalTimestamp = (nextIntervalTimestamp: number) =>
  Math.ceil(Date.now() / nextIntervalTimestamp) * nextIntervalTimestamp;

function getFromMemoryCache<T>(key: string): T | undefined {
  const entry = memoryCache.get(key);

  // inflight promise
  if (!entry || entry instanceof Promise) {
    return undefined;
  }

  if (Date.now() < entry.expiresAt) {
    return entry.data;
  }

  memoryCache.delete(key);
  return undefined;
}

async function executeCallback<T>(key: string, callback: Function): Promise<T | null> {
  const callbackValue = await callback();

  if (callbackValue) {
    // console.log(`[CACHE] Callback executed for ${key}`); // eslint-disable-line
    memoryCache.set(key, { data: callbackValue, expiresAt: getNextIntervalTimestamp(FIVE_MIN_IN_MS) });
  }

  return callbackValue;
}

function isCacheDisabled(): boolean {
  return typeof process === 'undefined' || process.env.BUILD_ENV === 'develops';
}

async function asyncGet<T>(key: string, callback: Function): Promise<T | null> {
  // const t1 = performance.now();
  if (isCacheDisabled()) {
    return callback();
  }

  // In-flight promise — await and return, fall through on rejection
  const inflight = memoryCache.get(key);
  if (inflight instanceof Promise) {
    try {
      // console.log(`[CACHE] In flight waiting for ${key}`); // eslint-disable-line
      const result = await inflight;
      // console.log(`[CACHE] In flight result for ${key}: ${(performance.now() - t1).toFixed(2)}ms`); // eslint-disable-line
      return result;
    } catch {
      // do nothing
    }
  }

  const memoryCached = getFromMemoryCache<T>(key);
  if (memoryCached !== undefined) {
    // console.log(`[CACHE] Memory hit for ${key}: ${(performance.now() - t1).toFixed(2)}ms`); // eslint-disable-line
    return memoryCached;
  }

  // Store promise synchronously before any await to coalesce concurrent requests
  const fetchPromise = (async (): Promise<T | null> => {
    try {
      return await executeCallback<T>(key, callback);
    } catch (err) {
      logError(err);
      return callback();
    } finally {
      // Clean up if still a promise (not replaced by resolved MemoryCacheEntry)
      if (memoryCache.get(key) instanceof Promise) {
        memoryCache.delete(key);
      }
    }
  })();

  memoryCache.set(key, fetchPromise);

  return fetchPromise;
}

export function ssrCache() {
  return {
    get<T>(key: string, callback: Function): Promise<T | null> {
      return asyncGet<T>(key, callback);
    },
  };
}

// Clean up expired memory cache entries, nightly invalidation
function cleanupMemoryCache() {
  const now = Date.now();

  for (const [key, entry] of memoryCache.entries()) {
    // skip in-flight promises
    if (entry instanceof Promise) {
      continue;
    }
    if (now >= entry.expiresAt) {
      memoryCache.delete(key);
    }
  }
}
// Run memory cleanup at the start of every 5th minute, to ensure correct sync with external nightly invalidation
if (typeof process !== 'undefined') {
  const scheduleNextCleanup = () => {
    setTimeout(
      () => {
        cleanupMemoryCache();
        scheduleNextCleanup();
      },
      getNextIntervalTimestamp(FIVE_MIN_IN_MS) - Date.now(),
    );
  };
  scheduleNextCleanup();
}
